import { useState, useEffect, useMemo } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, GraphicComponent } from 'echarts/components';
import { BarChart, LineChart, RadarChart, HeatmapChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { usePostAiAssuranceMutation } from './data-access/project.api';
import { computeMetrics, getHeatmapMatrix, getGovernanceTrendByDate } from '../ai-assurance/aiAssurance.utils';
import type { AiAssuranceMetrics } from '../ai-assurance/aiAssurance.utils';
import {
  getScorecardGaugeOption,
  getRadarOption,
  getHeatmapOption,
  getGovernanceTrendOption
} from '../ai-assurance/aiAssuranceCharts';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent,
  BarChart,
  LineChart,
  RadarChart,
  HeatmapChart,
  CanvasRenderer
]);

const DEFAULT_PERIOD_LABEL = 'Q4 2025';
const TARGET_MATURITY = 80;

interface AIAssuranceTabProps {
  projectId: string;
}

const AIAssuranceTab = ({ projectId }: AIAssuranceTabProps) => {
  const [periodLabel] = useState(DEFAULT_PERIOD_LABEL);
  const [metrics, setMetrics] = useState<AiAssuranceMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [postAiAssurance, { data: apiData, isLoading, isError, error: apiError }] = usePostAiAssuranceMutation();

  useEffect(() => {
    if (!projectId) return;
    postAiAssurance({
      projectId,
      periodLabel,
      latestOnly: true
    });
  }, [projectId, periodLabel]);

  useEffect(() => {
    if (!apiData?.fact_ai_assurance_360?.length) {
      setMetrics(null);
      return;
    }
    const m = computeMetrics(apiData.fact_ai_assurance_360, TARGET_MATURITY);
    setMetrics(m);
  }, [apiData]);

  useEffect(() => {
    if (isError && apiError) {
      const msg = 'message' in apiError ? String(apiError.message) : 'Failed to load AI Assurance data';
      setError(msg);
    } else {
      setError(null);
    }
  }, [isError, apiError]);

  const plot1Option = useMemo(() => (metrics ? getScorecardGaugeOption(metrics) : null), [metrics]);
  const plot2Option = useMemo(() => (metrics ? getRadarOption(metrics, TARGET_MATURITY) : null), [metrics]);
  const { dims: heatmapDims, mat: heatmapMat } = useMemo(
    () => (metrics ? getHeatmapMatrix(metrics.rows) : { dims: [], mat: [] }),
    [metrics]
  );
  const plot3Option = useMemo(() => {
    if (heatmapDims.length === 0) return null;
    return getHeatmapOption(
      heatmapDims.map(d => d[0]),
      heatmapMat
    );
  }, [heatmapDims, heatmapMat]);
  const trendData = useMemo(() => (metrics ? getGovernanceTrendByDate(metrics.rows) : null), [metrics]);
  const plot4Option = useMemo(
    () =>
      trendData && trendData.dates.length > 0
        ? getGovernanceTrendOption(trendData.dates, trendData.approved, trendData.conditional, trendData.rejected)
        : null,
    [trendData]
  );

  if (isLoading && !metrics) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="text-muted mt-2 mb-0">Loading AI Assurance data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body>
          <p className="text-danger mb-0">{error}</p>
        </Card.Body>
      </Card>
    );
  }

  if (apiData && !apiData.fact_ai_assurance_360?.length) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body>
          <p className="text-muted mb-0">No AI Assurance records for this project.</p>
        </Card.Body>
      </Card>
    );
  }

  if (!metrics) return null;

  return (
    <div>
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <h5 className="fw-semibold mb-3">Enterprise AI Assurance — {periodLabel}</h5>
          <p className="small text-muted mb-2">
            Maturity Index: <strong>{metrics.maturity_index}</strong> / 100 &nbsp;|&nbsp; Level:{' '}
            <strong>{metrics.maturity_level}</strong> &nbsp;|&nbsp; Target: {metrics.target} ({metrics.target_status})
          </p>
          <p className="small text-muted mb-2">
            Confidence: {metrics.confidence} &nbsp;|&nbsp; Projects: {metrics.total_projects} &nbsp;|&nbsp; Review
            events: {metrics.total_reviews}
          </p>
          <p className="small text-muted mb-0">
            Reviewer outcomes: Approved={metrics.approved}, Conditional={metrics.conditional}, Rejected=
            {metrics.rejected}
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-2">Plot 1 — Enterprise Scorecard Gauge</h6>
              {plot1Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot1Option}
                  notMerge
                  style={{ width: '100%', height: '200px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No gauge data.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-2">Plot 2 — 360° Radar (Current vs Target)</h6>
              {plot2Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot2Option}
                  notMerge
                  style={{ width: '100%', height: '340px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No radar data.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-2">Plot 3 — Ethical & Security Risk Heatmap</h6>
              {plot3Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot3Option}
                  notMerge
                  style={{ width: '100%', height: '340px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No heatmap data.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-2">Plot 4 — Governance Stability Trend</h6>
              {plot4Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot4Option}
                  notMerge
                  style={{ width: '100%', height: '300px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No trend data (need review_timestamp_utc).</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AIAssuranceTab;
